import React, { useState, useRef, useEffect, useMemo } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import './Menu.css';
import logo from '../../images/audithippo.png';
import { useConnectionSyncStatus } from '../../api/useConnectionSyncStatus';
import { filterBusinessUnits } from '../../api/businessUnits';
import { useBusinessUnits } from '../../api/useBusinessUnits';

const NAV_LINKS = [
    { key: 'journeys',         label: 'Journeys',         path: '/journeys' },
    { key: 'data-extensions',  label: 'Data Extensions',  path: '/data-extensions' },
    { key: 'automations',      label: 'Automations',      path: '/automations' },
    { key: 'email-studio',     label: 'Email Studio',     path: '/email-studio' },
    { key: 'cloud-pages',      label: 'Cloud Pages',      path: '/cloud-pages' },
    { key: 'audithippo-users', label: 'Users',            path: '/audithippo-users' },
];

const MONITORING_LINKS = [
    { key: 'automation-health', label: 'Automation Health',   path: '/automation-health' },
    { key: 'journey-health',    label: 'Journey Health',      path: '/journey-health' },
    { key: 'subscriber-health', label: 'Subscriber Health',   path: '/subscriber-health' },
    { key: 'pii-compliance',    label: 'PII Compliance',      path: '/pii-compliance' },
    { key: 'bu-comparison',     label: 'Cross-BU Comparison', path: '/bu-comparison' },
];

const MONITORING_PATHS = MONITORING_LINKS.map(l => l.path);

const Caret = ({ className }) => (
    <svg className={className} width="12" height="12" viewBox="0 0 24 24"
         fill="none" stroke="currentColor" strokeWidth="2.5">
        <path d="M6 9l6 6 6-6" />
    </svg>
);

const Brand = ({ onClick }) => (
    <NavLink to="/automations" className="logobock brand-block" onClick={onClick}>
        <div className="hippolog">
            <img src={logo} alt="AuditHippo" />
        </div>
        <span className="brand-copy">
            <span className="brand-name">
                Audit<span className="hippotxt">Hippo</span>
            </span>
            <span className="marketing-cloud-text">Marketing Cloud</span>
        </span>
    </NavLink>
);

function Menu() {
    const [menuOpen, setMenuOpen]               = useState(false);
    const [monitoringOpen, setMonitoringOpen]   = useState(false);
    const [profileOpen, setProfileOpen]         = useState(false);
    const [mobileMonOpen, setMobileMonOpen]     = useState(false);

    const [buQuery, setBuQuery]     = useState('');

    const monitoringRef = useRef(null);
    const profileRef    = useRef(null);
    const buSearchRef   = useRef(null);

    const navigate = useNavigate();
    const location = useLocation();

    const handleLogout = () => {
        localStorage.removeItem('isLoggedIn');
        localStorage.removeItem('userAuthToken');
        localStorage.removeItem('userSessionId');
        localStorage.removeItem('userConnectionId');
        localStorage.removeItem('userConnectionName');
        localStorage.removeItem('userConnectionSyncStatus');
        navigate('/login');
    };

    const syncStatus = useConnectionSyncStatus();
    const isSyncing  = syncStatus === 'Sync-In-Progress';

    const {
        units:   businessUnits,
        loading: buLoading,
        error:   buError,
        selectedBu,
        selectBu,
    } = useBusinessUnits();

    const visibleBus = useMemo(
        () => filterBusinessUnits(businessUnits, buQuery),
        [businessUnits, buQuery]
    );

    const monitoringActive = MONITORING_PATHS.includes(location.pathname);

    const handleSelectBu = unit => {
        selectBu(unit);
        setProfileOpen(false);
    };

    // Clear the filter and focus the search box each time the panel opens
    useEffect(() => {
        if (!profileOpen) return;
        setBuQuery('');
        const id = window.setTimeout(() => {
            if (buSearchRef.current) buSearchRef.current.focus();
        }, 0);
        return () => window.clearTimeout(id);
    }, [profileOpen]);

    // Close every dropdown whenever the route changes
    useEffect(() => {
        setMonitoringOpen(false);
        setProfileOpen(false);
        setMenuOpen(false);
    }, [location.pathname]);

    // Close on outside click / Escape
    useEffect(() => {
        if (!monitoringOpen && !profileOpen) return undefined;

        const handlePointer = e => {
            if (monitoringRef.current && !monitoringRef.current.contains(e.target)) {
                setMonitoringOpen(false);
            }
            if (profileRef.current && !profileRef.current.contains(e.target)) {
                setProfileOpen(false);
            }
        };
        const handleKey = e => {
            if (e.key === 'Escape') {
                setMonitoringOpen(false);
                setProfileOpen(false);
            }
        };

        document.addEventListener('mousedown', handlePointer);
        document.addEventListener('keydown', handleKey);
        return () => {
            document.removeEventListener('mousedown', handlePointer);
            document.removeEventListener('keydown', handleKey);
        };
    }, [monitoringOpen, profileOpen]);

    return (
        <>
            {/* Mobile overlay */}
            <div
                className={`mobile-menu-overlay${menuOpen ? ' show' : ''}`}
                onClick={() => setMenuOpen(false)}
            />

            {/* Mobile slide menu */}
            <div className={`mobile-menu${menuOpen ? ' show' : ''}`}>
                <div className="mobile-menu-header">
                    <Brand onClick={() => setMenuOpen(false)} />
                    <div className="mobile-menu-close" onClick={() => setMenuOpen(false)}>✕</div>
                </div>
                <div className="mobile-menu-items">
                    {NAV_LINKS.map(l => (
                        <NavLink
                            key={l.key}
                            to={l.path}
                            className={({ isActive }) =>
                                `mobile-menu-item${isActive ? ' active' : ''}`
                            }
                            onClick={() => setMenuOpen(false)}
                        >
                            {l.label}
                        </NavLink>
                    ))}

                    {/* Monitoring group */}
                    <button
                        type="button"
                        className={`mobile-menu-item mobile-menu-group${monitoringActive ? ' active' : ''}`}
                        aria-expanded={mobileMonOpen}
                        onClick={() => setMobileMonOpen(o => !o)}
                    >
                        Monitoring
                        <Caret className={`nav-dropdown-caret${mobileMonOpen ? ' is-open' : ''}`} />
                    </button>
                    {mobileMonOpen && (
                        <div className="mobile-menu-sub">
                            {MONITORING_LINKS.map(l => (
                                <NavLink
                                    key={l.key}
                                    to={l.path}
                                    className={({ isActive }) =>
                                        `mobile-menu-subitem${isActive ? ' active' : ''}`
                                    }
                                    onClick={() => setMenuOpen(false)}
                                >
                                    {l.label}
                                </NavLink>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Top navbar */}
            <nav className="navbar navbar-custom">
                <div className="container container-custom">
                    <div className="d-flex align-items-center justify-content-between w-100 flex-wrap gap-2">
                        <div className="d-flex align-items-center gap-3">
                            <button
                                className="mobile-menu-btn"
                                onClick={() => setMenuOpen(true)}
                                aria-label="Open menu"
                            >
                                <span /><span /><span />
                            </button>
                            <div className="d-flex align-items-center gap-2">
                                <Brand />
                                <span className="divider-mobile" />
                            </div>
                            <ul className="nav nav-pills-custom d-none d-lg-flex">
                                {NAV_LINKS.map(l => (
                                    <li key={l.key} className="nav-item">
                                        <NavLink
                                            to={l.path}
                                            className={({ isActive }) =>
                                                `nav-link${isActive ? ' active' : ''}`
                                            }
                                        >
                                            {l.label}
                                        </NavLink>
                                    </li>
                                ))}

                                {/* Monitoring dropdown */}
                                <li className="nav-item nav-dropdown-item" ref={monitoringRef}>
                                    <button
                                        type="button"
                                        className={`nav-link nav-dropdown-toggle${monitoringActive ? ' active' : ''}`}
                                        aria-expanded={monitoringOpen}
                                        aria-haspopup="true"
                                        onClick={() => {
                                            setProfileOpen(false);
                                            setMonitoringOpen(o => !o);
                                        }}
                                    >
                                        Monitoring
                                        <Caret className={`nav-dropdown-caret${monitoringOpen ? ' is-open' : ''}`} />
                                    </button>
                                    {monitoringOpen && (
                                        <ul className="nav-dropdown-menu">
                                            {MONITORING_LINKS.map(l => (
                                                <li key={l.key}>
                                                    <NavLink
                                                        to={l.path}
                                                        className={({ isActive }) =>
                                                            `nav-dropdown-link${isActive ? ' active' : ''}`
                                                        }
                                                        onClick={() => setMonitoringOpen(false)}
                                                    >
                                                        {l.label}
                                                    </NavLink>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </li>
                            </ul>
                        </div>

                        {/* Profile dropdown */}
                        <div className="header-actions">
                            <div className="header-dd header-profile-dd" ref={profileRef}>
                                <button
                                    type="button"
                                    className="header-profile-trigger"
                                    aria-expanded={profileOpen}
                                    aria-haspopup="true"
                                    onClick={() => {
                                        setMonitoringOpen(false);
                                        setProfileOpen(o => !o);
                                    }}
                                >
                                    <span className="header-user-avatar" aria-hidden="true">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                                             stroke="currentColor" strokeWidth="2"
                                             strokeLinecap="round" strokeLinejoin="round">
                                            <circle cx="12" cy="8" r="4" />
                                            <path d="M4 20a8 8 0 0 1 16 0" />
                                        </svg>
                                    </span>
                                    <span className="header-profile-copy">
                                        <span className="header-profile-name">
                                            {selectedBu ? selectedBu.name : 'Account'}
                                        </span>
                                        {selectedBu && selectedBu.mid && (
                                            <span className="header-profile-mid">— MID {selectedBu.mid}</span>
                                        )}
                                    </span>
                                    <Caret className={`nav-dropdown-caret${profileOpen ? ' is-open' : ''}`} />
                                </button>

                                {profileOpen && (
                                    <div className="header-dd-panel header-bu-panel">
                                        <div className="header-bu-search">
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                                                 stroke="#94A3B8" strokeWidth="2">
                                                <circle cx="11" cy="11" r="8" />
                                                <path d="m21 21-4.35-4.35" />
                                            </svg>
                                            <input
                                                ref={buSearchRef}
                                                type="text"
                                                value={buQuery}
                                                onChange={e => setBuQuery(e.target.value)}
                                                placeholder="Search business units…"
                                                autoComplete="off"
                                                aria-label="Search business units"
                                            />
                                        </div>

                                        <div className="header-bu-list" role="listbox" aria-label="Business units">
                                            {visibleBus.length === 0 ? (
                                                <p className="header-bu-empty">
                                                    {buLoading
                                                        ? 'Loading business units…'
                                                        : buError
                                                            ? `Couldn’t load business units: ${buError}`
                                                            : businessUnits.length === 0
                                                                ? 'No business units available.'
                                                                : `No business units match “${buQuery}”.`}
                                                </p>
                                            ) : (
                                                visibleBus.map(unit => {
                                                    const isSelected = selectedBu && unit.id === selectedBu.id;
                                                    return (
                                                        <button
                                                            key={unit.id}
                                                            type="button"
                                                            role="option"
                                                            aria-selected={!!isSelected}
                                                            className={`header-bu-option${isSelected ? ' is-selected' : ''}`}
                                                            onClick={() => handleSelectBu(unit)}
                                                        >
                                                            <span className="header-bu-option-copy">
                                                                <span className="header-bu-option-name">
                                                                    {unit.name}
                                                                    {isSelected && isSyncing && (
                                                                        <span className="header-bu-syncing">• Syncing…</span>
                                                                    )}
                                                                </span>
                                                                {unit.mid && (
                                                                    <span className="header-bu-option-mid">MID {unit.mid}</span>
                                                                )}
                                                            </span>
                                                            <span className="header-bu-tick" aria-hidden="true">
                                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                                                                     stroke="currentColor" strokeWidth="2.5">
                                                                    <path d="M20 6L9 17l-5-5" />
                                                                </svg>
                                                            </span>
                                                        </button>
                                                    );
                                                })
                                            )}
                                        </div>

                                        <div className="header-bu-footer">
                                            <NavLink
                                                to="/bu-comparison"
                                                className="header-bu-compare"
                                                onClick={() => setProfileOpen(false)}
                                            >
                                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                                                     stroke="currentColor" strokeWidth="2">
                                                    <rect x="3" y="3" width="7" height="18" rx="1" />
                                                    <rect x="14" y="3" width="7" height="18" rx="1" />
                                                </svg>
                                                Compare All BUs
                                            </NavLink>
                                            <button
                                                type="button"
                                                className="header-bu-signout"
                                                onClick={handleLogout}
                                            >
                                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                                                     stroke="currentColor" strokeWidth="2"
                                                     strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                                                    <polyline points="16 17 21 12 16 7" />
                                                    <line x1="21" y1="12" x2="9" y2="12" />
                                                </svg>
                                                Sign out
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </nav>
        </>
    );
}

export default Menu;
